/**
 * TwitterEmbed
 *
 * Renders a tweet placeholder and lets Twitter widgets hydrate it.
 *
 * Primary responsibilities:
 * - Avoid rendering third-party HTML inside message content
 * - Load the Twitter widgets script once per page
 *
 * Dependencies:
 * - Twitter widgets.js
 */

import { useEffect } from 'react'

interface TwitterEmbedProps {
  /** Full tweet URL, e.g. https://twitter.com/user/status/123 */
  url: string
}

type TwitterWindow = Window & {
  twttr?: {
    widgets?: {
      load: () => void
    }
  }
}

export function TwitterEmbed({ url }: TwitterEmbedProps) {
  useEffect(() => {
    if (!url) return
    ensureTwitterWidgetsScript()
    const twitterWindow = window as TwitterWindow
    twitterWindow.twttr?.widgets?.load()
  }, [url])

  if (!url) {
    return null
  }

  return (
    <div className="my-4">
      <blockquote className="twitter-tweet">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-blue-500 underline"
        >
          View post on Twitter
        </a>
      </blockquote>
    </div>
  )
}

function ensureTwitterWidgetsScript() {
  if (typeof window === 'undefined') return
  const twitterWindow = window as TwitterWindow
  const existing = document.querySelector<HTMLScriptElement>(
    'script[src^="https://platform.twitter.com/widgets.js"]'
  )
  if (existing) {
    twitterWindow.twttr?.widgets?.load()
    return
  }
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://platform.twitter.com/widgets.js'
  script.onload = () => twitterWindow.twttr?.widgets?.load()
  document.body.appendChild(script)
}
